import { ComponentType, forwardRef, useMemo } from 'react';

import { toEfficientHash } from '@givebacks/utils/strings';

const PROPS_TO_INCLUDE = ['variant', 'size', 'data', 'color'];

const getChildrenId = (children: React.ReactNode) => {
  if (typeof children === 'string') {
    return children?.toLocaleLowerCase().replace(/\s/g, '-');
  }
  return undefined;
};

const getPropsAsString = (props: Record<string, string>) => {
  const propsWithSelectedValues = Object.entries(props)
    .filter(([key]) => PROPS_TO_INCLUDE.includes(key))
    .filter(([, value]) => typeof value === 'string');
  const mappedProps = propsWithSelectedValues.map(([key, value]) => `${key}-${value}`).join('-');
  return mappedProps;
};
const createPropString = (props: Record<string, string>) => {
  const childrenId = getChildrenId(props.children);
  const propsAsString = getPropsAsString(props);
  // If there are no children or props, return undefined
  if (childrenId === undefined) {
    return undefined;
  }
  if (propsAsString === '') {
    return childrenId;
  }

  return `${childrenId}-${propsAsString}`;
};

const withUiDataAttributeProps = <TProps extends Record<string, any>>(props: TProps) => {
  const { 'data-tracking-id': trackingId } = props;

  return {
    trackingId: trackingId,
    ...props,
  };
};

const useUiDataAttributes = <TProps extends Record<string, string>>(
  { trackingId, ...props }: TProps,
  prefix?: string
) => {
  const uniqueDataId = useMemo(() => {
    try {
      const stringProps = createPropString(props);
      if (!stringProps) {
        return undefined;
      }
      const finalString = prefix ? `${prefix}-${stringProps}` : stringProps;

      return toEfficientHash(finalString).toString();
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only utilize the prefix once
  }, []);

  return {
    'data-gb-ui-generated-id': uniqueDataId,
    'data-gb-ui-tracking-id': trackingId ?? undefined,
  };
};

// Step 1: Define the generic component type
type GenericComponent<T> = React.ComponentType<T>;

const withWrapper = (Component: GenericComponent<any>, prefix: string) => {
  const withWrapper = forwardRef((props, ref) => {
    const { trackingId, ...rest } = withUiDataAttributeProps(props);
    try {
      const generatedProps = useUiDataAttributes({ trackingId, ...rest }, prefix);

      return <Component {...rest} {...generatedProps} ref={ref} />;
    } catch {
      // return the component without applying the generated ID if there is an error
      console.error('Error in applyUiId');
      return <Component {...rest} ref={ref} />;
    }
  });

  withWrapper.displayName = `withWrapper(${
    (Component.displayName || Component.name) ?? 'Wrapped'
  })`;
  return withWrapper;
};

const applyUiId = <T extends ComponentType<any>>(Component: T, prefix: string) => {
  // Create a new empty object to hold the component
  const _component = {} as T;

  // Create a wrapped version of the component
  const componentWIthWrapper = withWrapper(Component, prefix);

  // Assign the wrapped component to the new object
  Object.assign(_component, { ...Component, ...componentWIthWrapper });
  return _component as T;
};

export { applyUiId, useUiDataAttributes };
